import { useState, useRef, useEffect } from "react";
import { Modal } from ".";
import Alert from "../alert/Alert";
import { X, Lock } from "lucide-react";
import { request } from "../../../utils";

interface ResetPinModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ResetPinModal = ({ isOpen, onClose, onSuccess }: ResetPinModalProps) => {
  const [pin, setPin] = useState<string[]>(["", "", "", ""]);
  const [confirmPin, setConfirmPin] = useState<string[]>(["", "", "", ""]);
  const [otp, setOtp] = useState<string[]>(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const pinRefs = useRef<(HTMLInputElement | null)[]>([]);
  const confirmPinRefs = useRef<(HTMLInputElement | null)[]>( []);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [activeSection, setActiveSection] = useState<"pin" | "confirm" | "otp">("pin");

  useEffect(() => {
    if (isOpen) {
      resetAll();
      setActiveSection("pin");
      setTimeout(() => {
        pinRefs.current[0]?.focus();
      }, 100);
    }
  }, [isOpen]);

  const resetAll = () => {
    setPin(["", "", "", ""]);
    setConfirmPin(["", "", "", ""]);
    setOtp(["", "", "", ""]);
    setError("");
  };

  const handlePinChange = (index: number, value: string, type: "pin" | "confirm") => {
    if (!/^\d*$/.test(value)) return;
    
    if (type === "pin") {
      const newPin = [...pin];
      newPin[index] = value;
      setPin(newPin);
      
      if (value && index < 3) {
        setTimeout(() => {
          pinRefs.current[index + 1]?.focus();
        }, 10);
      }
      
      if (newPin.every(digit => digit !== "") && index === 3) {
        setTimeout(() => {
          setActiveSection("confirm");
          confirmPinRefs.current[0]?.focus();
        }, 10);
      }
    } else {
      const newConfirmPin = [...confirmPin];
      newConfirmPin[index] = value;
      setConfirmPin(newConfirmPin);
      
      if (value && index < 3) {
        setTimeout(() => {
          confirmPinRefs.current[index + 1]?.focus();
        }, 10);
      }
      
      if (newConfirmPin.every(digit => digit !== "") && index === 3) {
        setTimeout(() => {
          setActiveSection("otp");
          otpRefs.current[0]?.focus();
        }, 10);
      }
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    if (value && index < 5) {
      setTimeout(() => {
        otpRefs.current[index + 1]?.focus();
      }, 10);
    }
    
    if (newOtp.every(digit => digit !== "") && index === 5) {
      handleSubmit();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>, type: "pin" | "confirm" | "otp") => {
    if (e.key === "Backspace") {
      if (type === "pin") {
        if (!pin[index] && index > 0) {
          setTimeout(() => {
            pinRefs.current[index - 1]?.focus();
          }, 10);
        } else if (pin[index]) {
          const newPin = [...pin];
          newPin[index] = "";
          setPin(newPin);
        }
      } else if (type === "confirm") {
        if (!confirmPin[index] && index > 0) {
          setTimeout(() => {
            confirmPinRefs.current[index - 1]?.focus();
          }, 10);
        } else if (confirmPin[index]) {
          const newConfirmPin = [...confirmPin];
          newConfirmPin[index] = "";
          setConfirmPin(newConfirmPin);
        }
      } else {
        if (!otp[index] && index > 0) {
          setTimeout(() => {
            otpRefs.current[index - 1]?.focus();
          }, 10);
        } else if (otp[index]) {
          const newOtp = [...otp];
          newOtp[index] = "";
          setOtp(newOtp);
        }
      }
    }
  };

  const handleSubmit = async () => {
    const pinStr = pin.join("");
    const confirmPinStr = confirmPin.join("");
    const otpStr = otp.join("");

    if (pinStr.length !== 4) {
      setError("Please enter a complete 4-digit PIN");
      setActiveSection("pin");
      setTimeout(() => {
        pinRefs.current[0]?.focus();
      }, 10);
      return;
    }

    if (confirmPinStr.length !== 4) {
      setError("Please confirm your 4-digit PIN");
      setActiveSection("confirm");
      setTimeout(() => {
        confirmPinRefs.current[0]?.focus();
      }, 10);
      return;
    }

    if (pinStr !== confirmPinStr) {
      setError("PINs do not match");
      setActiveSection("pin");
      setPin(["", "", "", ""]);
      setConfirmPin(["", "", "", ""]);
      setTimeout(() => {
        pinRefs.current[0]?.focus();
      }, 10);
      return;
    }

    if (otpStr.length !== 4) {
      setError("Please enter the complete 4-digit OTP");
      setActiveSection("otp");
      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 10);
      return;
    }

    setLoading(true);
    setError("");

    try {
      await request.post({
        url: "/user/pin/reset",
        data: {
          pin: pinStr,
          otp: otpStr
        }
      });
      
      onSuccess();
    } catch (err: any) {
      setError(err.message || "Failed to reset PIN. Please try again.");
      resetAll();
      setTimeout(() => {
        pinRefs.current[0]?.focus();
      }, 10);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className="max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900"
    >
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
              <Lock className="h-5 w-5 text-primary dark:text-white/90" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Reset PIN
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Set new PIN and enter OTP
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
            type="button"
            disabled={loading}
          >
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6">
          <Alert variant="error" title="Error" message={error} />
        </div>
      )}

      {/* New PIN */}
      <div className="mb-8">
        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            New PIN
          </label>
          <div className="flex justify-center gap-3">
            {[0, 1, 2, 3].map((index) => (
              <input
                key={`pin-${index}`}
                ref={(el) => {
                  pinRefs.current[index] = el;
                }}
                type="text"
                maxLength={1}
                value={pin[index]}
                onChange={(e) => handlePinChange(index, e.target.value, "pin")}
                onKeyDown={(e) => handleKeyDown(index, e, "pin")}
                className={`h-14 w-14 rounded-lg border-2 text-center text-2xl font-bold tracking-widest transition-all focus:outline-none focus:ring-2 ${
                  activeSection === "pin"
                    ? "border-primary bg-gray-50 text-gray-800 focus:border-primary focus:ring-primary/20 dark:bg-gray-800 dark:text-white"
                    : "border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-800"
                }`}
                disabled={loading}
                inputMode="numeric"
              />
            ))}
          </div>
        </div>

        {/* Confirm PIN */}
        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Confirm PIN
          </label>
          <div className="flex justify-center gap-3">
            {[0, 1, 2, 3].map((index) => (
              <input
                key={`confirm-${index}`}
                ref={(el) => {
                  confirmPinRefs.current[index] = el;
                }}
                type="text"
                maxLength={1}
                value={confirmPin[index]}
                onChange={(e) => handlePinChange(index, e.target.value, "confirm")}
                onKeyDown={(e) => handleKeyDown(index, e, "confirm")}
                className={`h-14 w-14 rounded-lg border-2 text-center text-2xl font-bold tracking-widest transition-all focus:outline-none focus:ring-2 ${
                  activeSection === "confirm"
                    ? "border-primary bg-gray-50 text-gray-800 focus:border-primary focus:ring-primary/20 dark:bg-gray-800 dark:text-white"
                    : "border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-800"
                }`}
                disabled={loading}
                inputMode="numeric"
              />
            ))}
          </div>
        </div>

        {/* OTP */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            OTP (4 digits)
          </label>
          <div className="flex justify-center gap-2">
            {[0, 1, 2, 3].map((index) => (
              <input
                key={`otp-${index}`}
                ref={(el) => {
                  otpRefs.current[index] = el;
                }}
                type="text"
                maxLength={1}
                value={otp[index]}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e, "otp")}
                className={`h-14 w-14 rounded-lg border-2 text-center text-2xl font-bold tracking-widest transition-all focus:outline-none focus:ring-2 ${
                  activeSection === "otp"
                    ? "border-primary bg-gray-50 text-gray-800 focus:border-primary focus:ring-primary/20 dark:bg-gray-800 dark:text-white"
                    : "border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-800"
                }`}
                disabled={loading}
                inputMode="numeric"
              />
            ))}
          </div>
          <div className="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">
            Enter the 4-digit OTP sent to your email
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-3">
        <button
          onClick={handleSubmit}
          disabled={loading || pin.join("").length !== 4 || confirmPin.join("").length !== 4 || otp.join("").length !== 4}
          className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          {loading ? (
            <>
              Resetting PIN...
            </>
          ) : (
            "Reset PIN"
          )}
        </button>

        <button
          type="button"
          onClick={resetAll}
          className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          disabled={loading}
        >
          Clear All
        </button>
      </div>
    </Modal>
  );
};

export default ResetPinModal;